import React from 'react'

import { useI18n } from '../../../configuration/contexts/I18nContext'
import styled, { elementProps } from '../../constants/theme'
import { EyeIcon, EyeClosedIcon } from '../elements/Icons'
import {
  BasicType,
  ElementType,
  FormElementProps,
  QuestionType,
} from './FormElementParts'

const TEL_ALLOWED_CHARS = /[^\d().\-\s]/
const NUMBERS = /[^\d]/

export const formattedValue = (
  type: BasicType | ElementType | QuestionType,
  value: string | string[],
): string => {
  if (
    type !== ElementType.TEL &&
    type !== QuestionType.MULTISELECT &&
    (typeof value === 'string' || typeof value === 'undefined')
  ) {
    return value
  } else if (typeof value === 'object' && type === QuestionType.MULTISELECT) {
    return value.join(', ')
  } else {
    const areaCode = value.slice(0, 3)
    const firstThree = value.slice(3, 6)
    const lastFour = value.slice(6, 10)

    if (lastFour.length > 0) {
      return `(${areaCode}) ${firstThree}-${lastFour}`
    } else if (firstThree.length > 0) {
      return `(${areaCode}) ${firstThree}`
    } else if (areaCode.length > 0) {
      return `(${areaCode}`
    } else {
      return ''
    }
  }
}

export const getAttributeType = (
  type: BasicType | ElementType | QuestionType,
): string => {
  switch (type) {
    case ElementType.ZIP:
      return ElementType.TEL
    default:
      return type
  }
}

export const storedValue = (
  type: BasicType | ElementType | QuestionType,
  value: string,
): string => {
  switch (type) {
    case ElementType.TEL:
      return value.split(NUMBERS).join('').slice(0, 10)
    case ElementType.ZIP:
      return value.split(NUMBERS).join('').slice(0, 5)
    default:
      return value
  }
}

export const allowedInputValue = (
  type: BasicType | ElementType | QuestionType,
  value: string,
): string => {
  switch (type) {
    case ElementType.TEL:
      return value.split(TEL_ALLOWED_CHARS).join('')
    case ElementType.ZIP:
      return value.split(NUMBERS).join('').slice(0, 5)
    default:
      return value
  }
}

const InputElement: React.FC<FormElementProps> = ({
  disabled,
  icon,
  isFocused,
  isRequired,
  label,
  name,
  onChange,
  onInput,
  onKeyUp,
  onKeyDown,
  onPaste,
  pattern,
  placeholder,
  showFieldIcons = true,
  step,
  type,
  unifiedErrorText,
  value: incomingValue,
}) => {
  const [value, setValue] = React.useState<string>(
    formattedValue(type, incomingValue || ''),
  )
  const [revealPassword, setRevealPassword] = React.useState<boolean>(false)
  const i18n = useI18n()
  const className = value.length > 0 ? 'HasValue' : ''
  const isInvalid = !!(unifiedErrorText && unifiedErrorText.length > 0)
  let attributeType = getAttributeType(type)
  if (attributeType === ElementType.PASSWORD && revealPassword) {
    attributeType = ElementType.TEXT
  }

  if (!name) {
    return null
  }

  const handleChange = (e) => {
    e.preventDefault()
    const newValue = e.target.value
    setValue(allowedInputValue(type, newValue))

    if (onChange) {
      onChange({ name, value: storedValue(type, newValue) })
    }
  }

  const handleBlur = () => {
    setValue(formattedValue(type, storedValue(type, value)))
  }

  const toggleRevealPassword = () => {
    setRevealPassword(!revealPassword)
  }

  const ariaLabelKeyPrefix = revealPassword ? 'hide' : 'reveal'

  return (
    <Wrapper>
      {icon && showFieldIcons ? (
        <IconContainer>{icon}</IconContainer>
      ) : undefined}
      <Input
        aria-describedby={isInvalid ? `${label} Error` : undefined}
        aria-invalid={isInvalid}
        autoFocus={isFocused}
        className={className}
        disabled={disabled}
        id={name}
        isPasswordField={type === ElementType.PASSWORD}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onInput={onInput}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        pattern={pattern}
        placeholder={placeholder || undefined}
        required={isRequired}
        step={step}
        type={attributeType}
        value={value}
      />
      {type === ElementType.PASSWORD && (
        <Button
          aria-controls={name}
          aria-label={i18n.t(`${ariaLabelKeyPrefix}Password`)}
          aria-live="polite"
          onClick={toggleRevealPassword}
          type="button"
        >
          {revealPassword ? (
            <EyeClosedIcon height="20px" width="20px" />
          ) : (
            <EyeIcon height="20px" width="20px" />
          )}
        </Button>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div(({ theme }) => ({
  '&:hover': {
    borderColor: theme.primary,
  },
  '&:focus-within': {
    borderColor: theme.primary,
    boxShadow: `0 0 0 1px ${theme.primary}`,
  },
  '.invalid &': {
    borderColor: theme.error,
  },
  alignItems: 'center',
  backgroundColor: theme.background,
  borderColor: theme.border,
  borderRadius: theme.borderRadiusSmall,
  borderStyle: 'solid',
  borderWidth: 1,
  display: 'flex',
}))

const IconContainer = styled.div(({ theme }) => ({
  marginLeft: theme.paddingSmall,
  marginTop: theme.paddingSmall,
}))

const Input = styled.input<{ isPasswordField: boolean }>(
  {
    '&:-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
    '&[type="date"]': {
      paddingRight: 0,
      '&:-webkit-calendar-picker-indicator': {
        display: 'none',
        '&:WebkitApperance': 'none',
      },
    },
    ...elementProps,
    boxShadow: 'unset', // kills red around 'required' inputs in FF
    display: 'inline',
  },
  ({ theme, isPasswordField }) => ({
    '.invalid &': {
      color: theme.error,
      fontWeight: 'bold',
    },
    color: theme.text,
    fontFamily: theme.fontFamily,
    padding: theme.paddingSmall,
    ...(isPasswordField ? { width: 'calc(100% - 32px)' } : undefined), // Account for password show/hide icon
  }),
)

const Button = styled.button(({ theme }) => ({
  borderRadius: '50%',
  height: '27px', // Not theme-sized so that the input width can be calculated
  padding: '3px', // Not theme-sized so that the input width can be calculated
  width: '27px', // Not theme-sized so that the input width can be calculated

  '&:hover': {
    backgroundColor: theme.hover,
  },
  '&:focus': {
    backgroundColor: theme.hover,
    boxShadow: `0 0 0 1px ${theme.primary}`,
  },
  '&:active': {
    backgroundColor: theme.activeBackgroundColor,
  },
}))

Input.displayName = 'Input'

export default InputElement
